import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { SalesTransaction } from "../types";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { logger } from "../lib/logger";
import { GLOBAL_DATE_RANGE_KEY, DATE_RANGE_CHANGE_EVENT } from "../hooks/useGlobalDateRange";
import { timestampToOrgDateKey, toOrgDateKey } from "../lib/timezone";

// =============================================================================
// TransactionContext - Transaction Data with 5-Minute TTL Cache
// =============================================================================
// PURPOSE: Manages transaction data and daily stats with localStorage caching.
//
// ARCHITECTURE:
// - Dynamic prefetch window based on user's selected date range (min 93 days)
// - 5-minute TTL cache in localStorage (org-scoped to prevent cross-tenant leaks)
// - AbortController cleanup prevents race conditions on rapid org switching
// - Derives daily stats locally from transaction data (no RPC needed)
//
// KEY DECISIONS:
// - Dynamic prefetch: reads date range from localStorage, adds 7-day buffer
// - Cache key includes orgId + prefetch days: invalidates when range expands
// - Daily stats computed in-memory: faster than DB aggregation
// - Only fetches sales_call_outcome and low_ticket_purchase (not webinar signups)
// - Transactions stored in camelCase (transformed from snake_case DB schema)
// =============================================================================

import { CACHE_VERSION } from "../lib/cacheVersion";

// --- Cache Configuration ---
const CACHE_PREFIX = `altara_${CACHE_VERSION}_transactions_`;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MIN_PREFETCH_DAYS = 93; // Minimum prefetch window (covers timezone offsets + 2-month default)
const PREFETCH_BUFFER_DAYS = 7; // Extra buffer beyond selected date range
const MAX_PREFETCH_DAYS = 737; // hard cap: 730d All Time + 7d buffer (prevents payload blowup — D3)

// Dynamically calculate prefetch days based on user's selected date range in localStorage.
// Without this, the date picker can show dates outside the prefetch window, causing silent data loss.
const getRequiredPrefetchDays = (): number => {
  try {
    const stored = localStorage.getItem(GLOBAL_DATE_RANGE_KEY);
    if (!stored) return MIN_PREFETCH_DAYS;

    const parsed = JSON.parse(stored);
    if (!parsed.start) return MIN_PREFETCH_DAYS;

    const startDate = new Date(parsed.start);
    if (isNaN(startDate.getTime())) return MIN_PREFETCH_DAYS;

    const daysSinceStart = Math.ceil((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.min(
      Math.max(daysSinceStart + PREFETCH_BUFFER_DAYS, MIN_PREFETCH_DAYS),
      MAX_PREFETCH_DAYS
    );
  } catch {
    return MIN_PREFETCH_DAYS;
  }
};

// Read the current date range bounds from localStorage as YYYY-MM-DD strings.
// Returns null for each bound if not set (e.g. All Time mode before Wave 4 lands).
const getDateRangeBounds = (): { startISO: string | null; endISO: string | null } => {
  try {
    const stored = localStorage.getItem(GLOBAL_DATE_RANGE_KEY);
    if (!stored) return { startISO: null, endISO: null };

    const parsed = JSON.parse(stored);
    const startISO = parsed.start ? toOrgDateKey(new Date(parsed.start)) : null;
    const endISO = parsed.end ? toOrgDateKey(new Date(parsed.end)) : null;
    return { startISO, endISO };
  } catch {
    return { startISO: null, endISO: null };
  }
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

// Cache helpers
const getCache = <T,>(key: string, ttl: number, orgId: string): T | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const isExpired = Date.now() - entry.timestamp > ttl;
    const isSameOrg = entry.orgId === orgId;

    if (isExpired || !isSameOrg) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

const setCache = <T,>(key: string, data: T, orgId: string): void => {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    logger.warn("Transaction cache write failed");
  }
};

export const invalidateTransactionCache = (orgId: string): void => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const entry = JSON.parse(raw);
            if (entry.orgId === orgId) {
              localStorage.removeItem(key);
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  } catch {
    // Ignore cleanup errors
  }
};

// --- Database Row Interfaces ---
interface DB_DailyStats {
  organization_id: string;
  stat_date: string;
  total_deals: number;
  total_revenue: number;
  total_cash: number;
}

interface DB_Transaction {
  id: string;
  created_at: string;
  organization_id: string;
  lead_id?: string;
  closer_id?: string;
  setter_id?: string;
  event_type: string;
  status: string;
  cash_collected: number;
  revenue_closed: number;
  event_date?: string;
  custom_data?: {
    closer_name?: string;
    setter_name?: string;
    fathom_link?: string;
    warmth_level?: string;
    outcome_notes?: string;
    quality_of_lead?: number;
    source_platform?: string;
  };
  email?: string;
  sales_program_sold?: string;
  sales_next_payment?: string;
  sales_next_payment_amount?: number;
  call_start_time?: string;
  call_booked_time?: string;
  call_id?: string;
  call_cancel_by?: string;
  call_cancel_reason?: string;
  // Joined
  organizations?: { name: string };
  closer?: { full_name: string };
  setter?: { full_name: string };
  leads?: { name: string; email: string };
}

interface TransactionContextType {
  dailyStats: DB_DailyStats[];
  rawTransactions: SalesTransaction[];
  isLoading: boolean;
  setTransactions: (transactions: SalesTransaction[]) => void;
}

export const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

interface TransactionProviderProps {
  children: ReactNode;
  organizationId: string | null;
  orgTimezone: string | null;
  forceRefresh?: boolean;
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({
  children,
  organizationId,
  orgTimezone,
  forceRefresh = false,
}) => {
  const [rawTransactions, setRawTransactions] = useState<SalesTransaction[]>([]);
  const [dailyStats, setDailyStats] = useState<DB_DailyStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Track the prefetch window — expands reactively when date range moves further back
  const [prefetchDays, setPrefetchDays] = useState(() => getRequiredPrefetchDays());

  // Track current date range bounds for cache key construction.
  // Changing the range (even to same-size window at a different position) produces
  // a new cache key, which forces a re-fetch instead of serving stale data.
  const [dateRangeBounds, setDateRangeBounds] = useState(() => getDateRangeBounds());

  // Listen for date range changes — expand the prefetch window if needed AND update bounds
  const handleDateRangeChange = useCallback(() => {
    const needed = getRequiredPrefetchDays();
    setPrefetchDays((current) => {
      if (needed > current) {
        logger.debug(`Expanding prefetch window: ${current} → ${needed} days`);
        return needed;
      }
      return current;
    });
    // Always update bounds so same-size windows at different positions get distinct cache keys
    setDateRangeBounds(getDateRangeBounds());
  }, []);

  useEffect(() => {
    window.addEventListener(DATE_RANGE_CHANGE_EVENT, handleDateRangeChange);
    return () => window.removeEventListener(DATE_RANGE_CHANGE_EVENT, handleDateRangeChange);
  }, [handleDateRangeChange]);

  // AbortController for cleanup (prevents race conditions on rapid org switching)
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const forceRefreshRef = useRef(forceRefresh);

  // Update force refresh ref when prop changes
  useEffect(() => {
    forceRefreshRef.current = forceRefresh;
  }, [forceRefresh]);

  useEffect(() => {
    // Abort any in-flight request to prevent race conditions
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();
    const signal = fetchAbortControllerRef.current.signal;

    const fetchTransactions = async () => {
      if (!organizationId || !isSupabaseConfigured || !supabase) {
        setRawTransactions([]);
        setDailyStats([]);
        return;
      }

      const shouldForceRefresh = forceRefreshRef.current;
      // Cache key encodes org + date bounds so same-size windows at different
      // positions (e.g. "Last 7 days" vs "Apr 1-7") get distinct cache slots.
      // Sentinel "null" when All Time mode has no explicit start/end.
      const cacheKey = `${organizationId}_${dateRangeBounds.startISO ?? "null"}_${dateRangeBounds.endISO ?? "null"}`;

      // Check cache first (unless forcing refresh)
      if (!shouldForceRefresh) {
        const cachedTransactions = getCache<SalesTransaction[]>(
          cacheKey,
          CACHE_TTL,
          organizationId
        );

        if (cachedTransactions) {
          logger.debug("Using cached transaction data");
          setRawTransactions(cachedTransactions);

          // Derive daily stats from cached transactions
          const statsMap = new Map<string, DB_DailyStats>();
          cachedTransactions.forEach((t) => {
            if (!t.timestamp) return;
            const d = new Date(t.timestamp);
            if (isNaN(d.getTime()) || d.getFullYear() === 1970) return;
            // Group by calendar day in org timezone (not browser-local)
            const dateStr = timestampToOrgDateKey(d.toISOString(), orgTimezone);
            if (!statsMap.has(dateStr)) {
              statsMap.set(dateStr, {
                organization_id: t.organization_id,
                stat_date: dateStr,
                total_deals: 0,
                total_revenue: 0,
                total_cash: 0,
              });
            }
            const stat = statsMap.get(dateStr)!;
            stat.total_cash += t.cashCollected;
            stat.total_revenue += t.revenueClosed;
            const outcome = t.callOutcome.toLowerCase().replace(/_/g, " ");
            // Deals = PIF + CPP only. Exclude paying_installment (recurring payment, not a win).
            const isInstallment =
              outcome.includes("paying installment") || outcome.includes("installment");
            if (
              !isInstallment &&
              (outcome.includes("closed") ||
                outcome.includes("paid in full") ||
                outcome.includes("payment plan") ||
                outcome === "paid")
            ) {
              stat.total_deals += 1;
            }
          });
          setDailyStats(
            Array.from(statsMap.values()).sort(
              (a, b) => new Date(a.stat_date).getTime() - new Date(b.stat_date).getTime()
            )
          );
          return; // Exit early, using cached data
        }
      }

      setIsLoading(true);
      try {
        logger.debug("Fetching fresh transaction data");

        // Calculate cutoff date dynamically based on user's selected date range
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - prefetchDays);

        // Fetch Transactions — SCO, LT, call_booked, call_cancelled
        // Window expands dynamically to cover user's selected date range.
        // Paginated: PGRST_MAX_ROWS silently caps any single response at 1000
        // rows; every 2026 month exceeds that for these event types, so an
        // unpaginated fetch truncates the window and every KPI computed from
        // this context undercounts (CLAUDE.md bug #15). The id tiebreaker keeps
        // page boundaries stable when many rows share an event_date.
        const PAGE_SIZE = 1000;

        // ONE builder for every page, so page 2 can never drift from page 1.
        // Explicit column list, NOT `*`. The .map() below is the projection
        // boundary: only these base columns are ever read, and the mapped
        // SalesTransaction is all the app sees downstream. `*` pulled ~20 extra
        // wide columns into the ORDER BY event_date/id sort payload for no reason,
        // a major contributor to this query's 120GB temp spill (see
        // [altara]_SUPABASE_PERFORMANCE_OVERHAUL.md, 2026-07-22). If the .map()
        // starts reading a new column, add it here too.
        // Capture into a local so TS keeps the non-null narrowing inside the closure
        // below (narrowing on the outer `supabase` binding does not flow into an
        // arrow function).
        const sb = supabase;
        if (!sb) return;

        const pageQuery = (offset: number, withCount: boolean) => {
          const q = sb
            .from("transactions")
            .select(
              `
              id,
              event_type,
              call_start_time,
              event_date,
              created_at,
              organization_id,
              email,
              revenue_closed,
              cash_collected,
              status,
              closer_id,
              setter_id,
              sales_program_sold,
              sales_next_payment,
              custom_data,
              organizations (name),
              closer:closer_id (full_name),
              setter:setter_id (full_name),
              leads:lead_id (name, email)
            `,
              withCount ? { count: "exact" } : undefined
            )
            .eq("organization_id", organizationId)
            .in("event_type", [
              "sales_call_outcome",
              "low_ticket_purchase",
              "call_booked",
              "call_cancelled",
              "sales_pre_call",
            ])
            // Fetch rows where EITHER event_date OR call_start_time is in range.
            // Without this, calls booked before the cutoff but scheduled within the
            // user's date range are silently missing from the dataset.
            .or(
              `event_date.gte.${cutoffDate.toISOString()},call_start_time.gte.${cutoffDate.toISOString()}`
            )
            // DETERMINISTIC TOTAL ORDER — load-bearing for parallel paging below.
            // event_date alone is not unique, so without the id tiebreaker two
            // OFFSET windows could overlap or skip rows. Both keys, both directions,
            // must stay identical across every page.
            .order("event_date", { ascending: false, nullsFirst: false })
            .order("id", { ascending: true });
          return q.range(offset, offset + PAGE_SIZE - 1);
        };

        // PARALLEL PAGINATION (2026-08-01). Was a serial `for` loop that awaited
        // page N before requesting N+1. At the 93-day MINIMUM prefetch window this
        // org already needs 7 pages, and All Time (737d) needs 15+, so the old shape
        // cost 7-15 SEQUENTIAL round-trips (~86ms each, measured warm) on EVERY page
        // load, app-wide, above the router. Now: page 0 carries `count: 'exact'`
        // (measured free — within noise of no count), which tells us the total up
        // front, then every remaining page is fetched concurrently. 7-15 waves -> 2.
        //
        // Row set and order are UNCHANGED: same builder, same deterministic
        // ORDER BY event_date DESC, id ASC, pages reassembled strictly by index.
        const first = await pageQuery(0, true);

        // Bail out if request was aborted (user switched orgs)
        if (signal.aborted) {
          logger.debug("Transaction fetch aborted - org switch detected");
          return;
        }
        if (first.error) throw first.error;

        const firstRows = (first.data ?? []) as unknown as DB_Transaction[];
        const data: DB_Transaction[] = [...firstRows];

        // Only page further when page 0 came back FULL. A short first page is the
        // whole result set, whatever `count` claims.
        if (firstRows.length === PAGE_SIZE) {
          const total = first.count ?? null;

          if (total === null) {
            // Count unavailable — fall back to the original serial walk. Correctness
            // over speed: never guess how many pages exist.
            logger.warn("TransactionContext: exact count unavailable, serial paging");
            for (let offset = PAGE_SIZE; ; offset += PAGE_SIZE) {
              const { data: page, error } = await pageQuery(offset, false);
              if (signal.aborted) return;
              if (error) throw error;
              if (!page || page.length === 0) break;
              data.push(...(page as unknown as DB_Transaction[]));
              if (page.length < PAGE_SIZE) break;
            }
          } else {
            const remaining: number[] = [];
            for (let offset = PAGE_SIZE; offset < total; offset += PAGE_SIZE) {
              remaining.push(offset);
            }
            // Cap concurrency so a 737-day All Time window can't open 15+ sockets at
            // once against a 2-core instance. Batches still collapse 15 waves to ~3.
            const MAX_CONCURRENT = 6;
            for (let i = 0; i < remaining.length; i += MAX_CONCURRENT) {
              const batch = remaining.slice(i, i + MAX_CONCURRENT);
              const results = await Promise.all(batch.map((off) => pageQuery(off, false)));
              if (signal.aborted) {
                logger.debug("Transaction fetch aborted - org switch detected");
                return;
              }
              for (const r of results) {
                if (r.error) throw r.error;
              }
              // Reassemble in offset order — Promise.all preserves input order, and
              // `batch` is ascending, so this appends pages in the same sequence the
              // serial loop produced.
              for (const r of results) {
                data.push(...((r.data ?? []) as unknown as DB_Transaction[]));
              }
            }
          }
        }

        if (data) {
          // 1. Map Transactions
          const mappedData: SalesTransaction[] = (data as unknown as DB_Transaction[]).map(
            (row) => ({
              id: row.id,
              // Attribution mode: ALL call-related events use call_start_time (when the call
              // is scheduled) instead of event_date (when the webhook/form fired). This ensures
              // the Call Summary shows metrics for calls IN the selected window, not actions
              // taken during the window for calls outside it. Only low_ticket_purchase uses
              // event_date (no call involved).
              timestamp:
                row.event_type === "call_booked" ||
                row.event_type === "call_cancelled" ||
                row.event_type === "sales_pre_call" ||
                row.event_type === "sales_call_outcome"
                  ? row.call_start_time || row.event_date || row.created_at || ""
                  : row.event_date || row.created_at || "",
              organization_id: row.organization_id,
              organization_name: row.organizations?.name || "Unknown Org",
              eventType: row.event_type,
              clientName: row.leads?.name || row.email || "Unknown",
              leadEmail: row.leads?.email || row.email || "",
              revenueClosed: Number(row.revenue_closed || 0),
              cashCollected: Number(row.cash_collected || 0),
              callOutcome: row.status || "pending",

              closerId: row.closer_id || null,
              setterId: row.setter_id || null,

              closerName: row.closer?.full_name || row.custom_data?.closer_name || "Unassigned",
              setterName: row.setter?.full_name || row.custom_data?.setter_name || "Unassigned",

              programSold: row.sales_program_sold || null,
              moneyLeft: 0,

              dateOfCall: row.event_date || null,
              nextPaymentDate: row.sales_next_payment || null,
              fathomLink: row.custom_data?.fathom_link || null,
              qaScore: row.custom_data?.quality_of_lead || null,
              qaNotes: row.custom_data?.outcome_notes || null,
            })
          );
          setRawTransactions(mappedData);
          setCache(cacheKey, mappedData, organizationId);

          // 2. Calculate Daily Stats Locally
          const statsMap = new Map<string, DB_DailyStats>();

          mappedData.forEach((t) => {
            if (!t.timestamp) return; // Skip if still empty
            const d = new Date(t.timestamp);
            if (isNaN(d.getTime()) || d.getFullYear() === 1970) return; // Skip invalid or 1970

            // Group by calendar day in org timezone (not browser-local)
            const dateStr = timestampToOrgDateKey(d.toISOString(), orgTimezone);
            if (!statsMap.has(dateStr)) {
              statsMap.set(dateStr, {
                organization_id: t.organization_id,
                stat_date: dateStr,
                total_deals: 0,
                total_revenue: 0,
                total_cash: 0,
              });
            }
            const stat = statsMap.get(dateStr)!;
            stat.total_cash += t.cashCollected;
            stat.total_revenue += t.revenueClosed;

            const outcome = t.callOutcome.toLowerCase().replace(/_/g, " ");
            // Deals = PIF + CPP only. Exclude paying_installment (recurring payment, not a win).
            const isInstallment =
              outcome.includes("paying installment") || outcome.includes("installment");
            if (
              !isInstallment &&
              (outcome.includes("closed") ||
                outcome.includes("paid in full") ||
                outcome.includes("payment plan") ||
                outcome === "paid")
            ) {
              stat.total_deals += 1;
            }
          });

          const sortedStats = Array.from(statsMap.values()).sort(
            (a, b) => new Date(a.stat_date).getTime() - new Date(b.stat_date).getTime()
          );
          setDailyStats(sortedStats);
        }
      } catch (err) {
        logger.error("Error fetching transaction data", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();

    // Cleanup: abort in-flight request on unmount or dependency change
    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [organizationId, prefetchDays, dateRangeBounds]);

  const contextValue = useMemo(
    () => ({
      dailyStats,
      rawTransactions,
      isLoading,
      setTransactions: setRawTransactions,
    }),
    [dailyStats, rawTransactions, isLoading, setRawTransactions]
  );

  return <TransactionContext.Provider value={contextValue}>{children}</TransactionContext.Provider>;
};
